import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { Box, Button, Grid, TextField, MenuItem, FormControl, InputLabel, Select } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { createCustomer, fetchCities, fetchAreasByCityId, fetchStates } from "redux/actions/customerMaster";
import { CustomerMasterData } from "types/models/customerMaster/CustomerMasterApp";
import { CityModel } from "types/models/CityModel";
import { AreaModel } from "types/models/AreaModel";
import { StateModel } from "types/models/StatesModel";
import SendLink from "./sendLink";

interface CustomerProps {
  Customer: CustomerMasterData;

  customerId: number | null;
  showSendLinkForm: boolean;

  setCustomerId: Dispatch<SetStateAction<number | null>>;
  setShowSendLinkForm: Dispatch<SetStateAction<boolean>>;
}

const leadSourceOptions = [
  "Website", "Online", "Sunpack/ Board", "Flyer/ Leaflet", "Friend/ Neighbour", "Newspaper",
  "Organic", "DST", "Self", "Standee", "Canopy", "Cold Call", "Other",
];

const CustomerCreate: React.FC<CustomerProps> = ({
  Customer,
  customerId,
  showSendLinkForm,
  setCustomerId,
  setShowSendLinkForm,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [cities, setCities] = useState<CityModel[]>([]);
  const [areas, setAreas] = useState<AreaModel[]>([]);
  const [states, setStates] = useState<StateModel[]>([]);
  const [formDisabled, setFormDisabled] = useState(false);

  const user = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const createdBy = user?.name || "Unknown User";

  useEffect(() => {
    dispatch<any>(fetchStates())
      .then((res: any) => {
        const rows = res?.data?.data || res?.data?.rows || [];
        setStates(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => console.error("Error fetching states:", err));
  }, [dispatch]);

  const handleStateChange = (stateId: string, setFieldValue: any) => {
    setFieldValue("state", stateId);
    dispatch<any>(fetchCities(Number(stateId))).then((res: any) => {
      const rows = res?.data?.data || [];
      setCities(Array.isArray(rows) ? rows : []);
    });
  };

  const handleCityChange = (cityId: string, setFieldValue: any) => {
    setFieldValue("city", cityId);
    setFieldValue("area", "");
    setAreas([]);
    dispatch<any>(fetchAreasByCityId(Number(cityId))).then((res: any) => {
      setAreas(Array.isArray(res) ? res : []);
    });
  };

  const validationSchema = Yup.object({
    contact_name: Yup.string().required("Customer Name is required"),
    contact_number: Yup.string().required("Mobile Number is required").matches(/^[0-9]{10}$/, "Mobile Number must be 10 digits"),
    contact_email: Yup.string().email("Invalid email format").required("Email ID is required"),
    city: Yup.string().required("City is required"),
    area: Yup.string().required("Area is required"),
    address: Yup.string().required("Address is required"),
    reference: Yup.string(),
  });

  const handleSubmit = async (values: any) => {
    const selectedCity = cities.find((city) => city.id === Number(values.city));
    const selectedArea = areas.find((area) => area.areaId === Number(values.area));
    const selectedState = states.find((state) => state.stateId === Number(values.state));

    const dataPayload: any = {
      contact_name: values.contact_name,
      contact_number: values.contact_number,
      contact_email: values.contact_email,
      city: selectedCity?.name || "",
      area: selectedArea?.area || "",
      address: values.address,
      reference: values.reference,
      remarks_at_the_time_of_generation: createdBy,
      lead_source: "CC",
      created_by: user?.username,
      state: selectedState?.stateName || "",
    };

    const newDovPayload: any = {
      name: values.contact_name,
      mobile: values.contact_number,
      email: values.contact_email,
      city: selectedCity?.name || "",
      area: selectedArea?.area || "",
      address: values.address,
      reference: values.reference,
      remarks_at_the_time_of_generation: createdBy,
      lead_source: "CC",
      created_by: user?.username,
      state: selectedState?.stateName || "",
    };

    try {
      const response = (await dispatch(createCustomer(dataPayload, newDovPayload))) as unknown as AxiosResponse<any>;

      // ✅ IMPORTANT: confirm ID path (this is the most common reason nothing shows)
      const payload = response?.data;

      const newId =
        payload?.data?.id ??        // ✅ production
        payload?.data?.data?.id ??  // older nested variant
        payload?.id;

      console.log("createCustomer response:", payload);
      console.log("newId resolved:", newId);

      // Prefer API body status, not HTTP status
      const ok = payload?.status === "success" || response?.status === 200 || response?.status === 201;

      if (ok && newId) {
        setCustomerId(Number(newId));
        setShowSendLinkForm(true);
        setFormDisabled(true);
      } else {
        console.error("Create/Update succeeded but ID missing:", { payload, newId });
      }
    } catch (error) {
      console.error("Error creating customer:", error);
    }
  };

  return (
    <Box sx={{ height: { md: "100%", xs: "auto" }, width: { md: "100%", xs: "auto" }, m: { md: 8, xs: 2 }, overflowY: { xs: "auto", md: "hidden" }, maxHeight: { xs: "100vh", md: "none" }, }}>
      <Grid sx={{ m: 2 }}>
        <Formik
          initialValues={{
            contact_name: Customer?.contact_name || "",
            contact_number: Customer?.contact_number || "",
            contact_email: Customer?.contact_email || "",
            state: Customer?.state || "",
            city: Customer?.city || "",
            area: Customer?.area || "",
            address: Customer?.address || "",
            reference: Customer?.reference || "",
            lead_source: Customer?.lead_source || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              {/* keep your fields same... */}
              <Grid container spacing={3} mb={3}> <Grid item xs={12} md={6}> <Field as={TextField} fullWidth label="Customer Name" name="contact_name" variant="outlined" disabled={formDisabled} error={touched.contact_name && !!errors.contact_name} helperText={<ErrorMessage name="contact_name" />} /> </Grid> <Grid item xs={12} md={6}> <Field as={TextField} fullWidth label="Mobile Number" name="contact_number" variant="outlined" disabled={formDisabled} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { const val = e.target.value; if (/^\d{0,10}$/.test(val)) { setFieldValue("contact_number", val); } }} error={touched.contact_number && !!errors.contact_number} helperText={<ErrorMessage name="contact_number" />} /> </Grid> </Grid> <Grid container spacing={3} mb={3}> <Grid item xs={12}> <Field as={TextField} fullWidth label="Email ID" name="contact_email" variant="outlined" disabled={formDisabled} error={touched.contact_email && !!errors.contact_email} helperText={<ErrorMessage name="contact_email" />} /> </Grid> </Grid> <Grid container spacing={3} mb={3}> <Grid item xs={6}> <TextField select fullWidth name="state" label="State" disabled={formDisabled} value={values.state} onChange={(e) => handleStateChange(e.target.value, setFieldValue)} helperText={<ErrorMessage name="state" />} > {states.map((st) => (<MenuItem key={st.stateId} value={st.stateId}> {st.stateName} </MenuItem>))} </TextField> </Grid> <Grid item xs={6}> <TextField select fullWidth name="city" label="City" disabled={formDisabled} value={values.city} onChange={(e) => handleCityChange(e.target.value, setFieldValue)} helperText={<ErrorMessage name="city" />} > {cities.map((city) => (<MenuItem key={city.id} value={city.id}> {city.name} </MenuItem>))} </TextField> </Grid> </Grid> <Grid container spacing={3} mb={3}> <Grid item xs={6}> <TextField select fullWidth name="area" label="Area" disabled={formDisabled} value={values.area} onChange={(e) => setFieldValue("area", e.target.value)} helperText={<ErrorMessage name="area" />} > {areas.map((area) => (<MenuItem key={area.areaId} value={area.areaId}> {area.area} </MenuItem>))} </TextField> </Grid> <Grid item xs={6}> <FormControl fullWidth variant="outlined" error={touched.reference && !!errors.reference} disabled={formDisabled} > <InputLabel id="reference-label">Reference</InputLabel> <Select labelId="reference-label" label="Reference" name="reference" value={values.reference} onChange={(e) => setFieldValue("reference", e.target.value)} > {leadSourceOptions.map((opt) => (<MenuItem key={opt} value={opt}> {opt} </MenuItem>))} </Select> {touched.reference && errors.reference && (<div style={{ color: "#d32f2f", marginTop: "0.25rem", fontSize: "0.75rem", }} > {errors.reference} </div>)} </FormControl> </Grid> </Grid> <Grid container spacing={3} mb={3}> <Grid item xs={12}> <Field as={TextField} fullWidth multiline minRows={2} label="Address" name="address" variant="outlined" disabled={formDisabled} error={touched.address && !!errors.address} helperText={<ErrorMessage name="address" />} /> </Grid> </Grid>
              {/* ✅ Submit/Cancel only before sendlink form */}
              {!showSendLinkForm && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                  <Button variant="outlined" color="secondary" onClick={() => navigate("/customerCare/list")}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              )}

              {/* ✅ SendLink only after lead created */}
              {customerId !== null && showSendLinkForm && (
                <SendLink customerId={customerId} />
              )}
            </Form>
          )}
        </Formik>
      </Grid>
    </Box>
  );
};

export default CustomerCreate;
